import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import { AstralPopoverComponent } from '../astral-popover/astral-popover.component';

export type AstralAccordionAppearance = 'default' | 'nested';

@Component({
  selector: 'astral-accordion',
  standalone: true,
  imports: [TranslatePipe, AstralPopoverComponent],
  templateUrl: './astral-accordion.component.html',
  styleUrls: ['./astral-accordion.component.scss'],
  animations: [
    trigger('panelVisibility', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('180ms cubic-bezier(0.2, 0, 0, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1 }),
        animate('140ms cubic-bezier(0.4, 0, 1, 1)', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class AstralAccordionComponent implements OnInit, AfterViewInit {
  private static nextId = 0;
  private readonly uid = `astral-accordion-${++AstralAccordionComponent.nextId}`;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly route: ActivatedRoute,
    private readonly destroyRef: DestroyRef
  ) {}

  @Input() header: string;
  @Input() helpText: string;
  @Input() translateHeader: boolean = true;
  @Input() indicator: boolean;
  @Input() counter: number;
  @Input() startExpanded: boolean = false;
  @Input() fragment?: string;
  @Input() appearance: AstralAccordionAppearance = 'default';
  @Input() allowWidthExpansion = false;

  @Output() stateChanged = new EventEmitter<boolean>();

  @ViewChild('accordion') accordion?: ElementRef<HTMLElement>;

  expanded = false;
  accordionWidth: number | null = null;
  readonly prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  get panelId(): string {
    return `${this.uid}-panel`;
  }

  get headerId(): string {
    return `${this.uid}-header`;
  }

  get chevronIconClass(): string {
    return resolveAstralIconClass('chevronRight') ?? '';
  }

  get displayCounter(): string | null {
    if (!this.counter) {
      return null;
    }

    return this.counter > 9 ? '9+' : `${this.counter}`;
  }

  ngOnInit(): void {
    this.expanded = this.startExpanded;
  }

  ngAfterViewInit(): void {
    this.scheduleWidthExpansionSync();

    this.route.fragment.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(fragment => {
      if (this.fragment && fragment === this.fragment) {
        if (!this.expanded) {
          this.expanded = true;
          this.stateChanged.emit(true);
          this.scheduleWidthExpansionSync();
        }

        setTimeout(() => {
          try {
            this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } catch {}
        }, 0);
      }
    });
  }

  toggleAccordion(): void {
    this.expanded = !this.expanded;
    if (!this.expanded) {
      this.resetWidthExpansion();
    } else {
      this.scheduleWidthExpansionSync();
    }
    this.stateChanged.emit(this.expanded);
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    this.scheduleWidthExpansionSync();
  }

  private scheduleWidthExpansionSync(): void {
    if (!this.allowWidthExpansion || !this.expanded) {
      return;
    }

    requestAnimationFrame(() => this.syncWidthExpansion());
  }

  private syncWidthExpansion(): void {
    const accordion = this.accordion?.nativeElement;
    const viewport = accordion?.ownerDocument?.documentElement?.clientWidth;
    if (!accordion || !viewport || !this.allowWidthExpansion || !this.expanded) {
      return;
    }

    const bounds = accordion.getBoundingClientRect();
    const viewportInset = this.getViewportInset(accordion);
    const availableWidth = viewport - bounds.left - viewportInset;

    this.accordionWidth = Math.round(availableWidth);

    this.changeDetectorRef.detectChanges();
  }

  private getViewportInset(accordion: HTMLElement): number {
    const root = accordion.ownerDocument?.documentElement;
    const spacing = root ? getComputedStyle(root).getPropertyValue('--spacing-m') : '';
    return Number.parseFloat(spacing) || 16;
  }

  private resetWidthExpansion(): void {
    this.accordionWidth = null;
  }
}
